import React, { useContext, useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import CommonApi from '../CommonApi';
import Breadcrumbs from '../Breadcrumbs';
import { formatDate, formatPrice, goToCart, isDev } from '../Helper';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'react-i18next';
import Cart from '../Cart';
import {
  locale,
  addLocale,
  updateLocaleOption,
  updateLocaleOptions,
  localeOption,
  localeOptions,
} from 'primereact/api';

const Checkout = (props) => {
  const [RequestedDeliveryDate, setRequestedDeliveryDate] = useState(new Date());

  const { t } = useTranslation();
  const [CustomerPO, setCustomerPO] = useState('');
  const [Notes, setNotes] = useState('');

  const commonCtx = useContext(CommonApi);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Drop Ship To form states
  const [dropShipName, setDropShipName] = useState('');
  const [dropShipStreet1, setDropShipStreet1] = useState('');
  const [dropShipStreet2, setDropShipStreet2] = useState('');
  const [dropShipZip, setDropShipZip] = useState('');
  const [dropShipCountry, setDropShipCountry] = useState(null);
  const [dropShipState, setDropShipState] = useState(null);
  const [dropShipCity, setDropShipCity] = useState('');

  // Validation states
  const [dropShipNameError, setDropShipNameError] = useState(false);
  const [dropShipStreet1Error, setDropShipStreet1Error] = useState(false);
  const [dropShipZipError, setDropShipZipError] = useState(false);
  const [dropShipCountryError, setDropShipCountryError] = useState(false);
  const [dropShipStateError, setDropShipStateError] = useState(false);
  const [dropShipCityError, setDropShipCityError] = useState(false);

  const countries = [
    { label: 'United States', value: 'USA', iso2: 'US' },
    { label: 'Canada', value: 'CAN', iso2: 'CA' },
  ];

  const [states, setStates] = useState([]);

  // Check if Drop Ship To tab should be visible (only for company numbers 6 and 7)
  const isDropShipVisible = () => {
    return commonCtx.dropShipTo === 'Y';
  };

  const isDropShipFormValid = () => {
    return (
      dropShipName &&
      dropShipStreet1 &&
      dropShipZip &&
      dropShipCountry &&
      dropShipState &&
      dropShipCity
    );
  };

  useEffect(() => {
    return () => {
      commonCtx.resetCalculation();
    };
  }, []); // eslint-disable-line

  // Restore form fields after page refresh if a prevalidation was in progress
  useEffect(() => {
    const p = commonCtx.prevalidationRestoredParams;
    if (!p) return;
    if (p.CustomerPO) setCustomerPO(p.CustomerPO);
    if (p.Notes) setNotes(p.Notes);
    if (p.RequestedDeliveryDate) setRequestedDeliveryDate(new Date(p.RequestedDeliveryDate));
    if (typeof p.activeTab === 'number') setActiveTab(p.activeTab);
    if (p.dropShipData) {
      const ds = p.dropShipData;
      if (ds.name) setDropShipName(ds.name);
      if (ds.street1) setDropShipStreet1(ds.street1);
      if (ds.street2) setDropShipStreet2(ds.street2);
      if (ds.zip) setDropShipZip(ds.zip);
      if (ds.country) setDropShipCountry(ds.country);
      if (ds.state) setDropShipState(ds.state);
      if (ds.city) setDropShipCity(ds.city);
    }
  }, [commonCtx.prevalidationRestoredParams]); // eslint-disable-line

  // Restore shipping address after page refresh — waits for addresses to be loaded first
  useEffect(() => {
    const p = commonCtx.prevalidationRestoredParams;
    if (!p?.shippingAddressId) return;
    if (!commonCtx.addresses?.length) return;
    if (commonCtx.selectedShippingAddress) return; // already selected
    commonCtx.selectShippingAddress(p.shippingAddressId);
  }, [commonCtx.prevalidationRestoredParams, commonCtx.addresses]); // eslint-disable-line

  useEffect(() => {
    if (!commonCtx.loading && loading && !commonCtx.loadingCart) {
      if (commonCtx.isLoggedIn) {
        commonCtx.listAddresses();
      }
      setLoading(false);
    }
  });

  useEffect(() => {
    if (dropShipCountry) {
      commonCtx
        .getStates(dropShipCountry, 'EN')
        .then((statesData) => {
          setStates(statesData);
          // Reset state selection when country changes
          setDropShipState(null);
        })
        .catch((error) => {
          console.error('Error loading states:', error);
          setStates([]);
        });
    } else {
      setStates([]);
      setDropShipState(null);
    }
  }, [dropShipCountry, commonCtx.language]);

  // Set DropShipTo tab as default if no regular shipping addresses are available
  useEffect(() => {
    if (!commonCtx.loading && !loading && isDropShipVisible()) {
      const regularShippingAddresses = commonCtx.addresses.filter(
        (address) => address.Billing === false && address.xp.DropShipTo !== 'Y'
      );

      if (regularShippingAddresses.length === 0) {
        setActiveTab(1); // Set DropShipTo tab as active
      }
    }
  }, [commonCtx.addresses, commonCtx.loading, loading]);

  if (isDev) {
    console.log(CustomerPO === '' ? true : false);
  }

  return (
    <>
      <Breadcrumbs parent="Cart" leaf="Checkout"></Breadcrumbs>
      <div className="layout-content grid">
        <div className="col-12 md:col-10">
          <div className="card card-w-title global-sales ui-fluid">
            <div className="grid">
              <div className="hidden col-12 mt-4 lg:mt-0 mb-4">
                <div className="text-900 font-medium text-xl">{t('BillingAddress')}</div>
              </div>
              <div className="hidden col-12 field mb-0">
                <label htmlFor="addresses" className="text-900 font-medium mb-3"></label>
                <Dropdown
                  value={commonCtx.activeCart?.BillingAddressID}
                  onChange={(e) => {
                    commonCtx.selectBillingAddress(e.value);
                  }}
                  className="p-inputtext w-full mb-3"
                  options={commonCtx.addresses.filter(
                    (address) => address.Billing === true && address.xp.DropShip !== 'Y'
                  )}
                  optionLabel="label"
                />
              </div>

              <div className="col-12 mt-4 lg:mt-0 mb-4">
                <div className="text-900 font-medium text-xl">{t('ShippingAddress')}</div>
              </div>

              <div className="col-12">
                {isDropShipVisible() ? (
                  <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    <TabPanel header={t('ShipToAddress')}>
                      <div className="grid">
                        <div className="col-12 field mb-0">
                          <label htmlFor="addresses" className="text-900 font-medium mb-3"></label>
                          <Dropdown
                            value={commonCtx.selectedShippingAddress?.ID}
                            onChange={(e) => {
                              commonCtx.selectShippingAddress(e.value);
                            }}
                            disabled={commonCtx.loadingCart}
                            className="p-inputtext w-full mb-3"
                            options={commonCtx.addresses.filter(
                              (address) =>
                                address.Billing === false && address.xp.DropShipTo !== 'Y'
                            )}
                            optionLabel="label"
                            placeholder={t('ChooseAddress')}
                          />
                        </div>
                      </div>
                    </TabPanel>

                    <TabPanel header={t('DropShipTo')}>
                      <div className="grid">
                        <div className="col-12 lg:col-6 field mb-3">
                          <label htmlFor="dropShipName" className="text-900 font-medium mb-2 block">
                            {t('Name')} *
                          </label>
                          <InputText
                            id="dropShipName"
                            value={dropShipName}
                            maxLength={30}
                            onChange={(e) => {
                              setDropShipName(e.target.value);
                              if (e.target.value) setDropShipNameError(false);
                            }}
                            onBlur={() => setDropShipNameError(!dropShipName)}
                            className={`w-full ${dropShipNameError ? 'p-invalid' : ''}`}
                          />
                          {dropShipNameError && (
                            <small className="p-error">{t('NameIsRequired')}</small>
                          )}
                        </div>

                        <div className="col-12 lg:col-6 field mb-3">
                          <label
                            htmlFor="dropShipStreet1"
                            className="text-900 font-medium mb-2 block"
                          >
                            {t('StreetAddress1')} *
                          </label>
                          <InputText
                            id="dropShipStreet1"
                            value={dropShipStreet1}
                            maxLength={100}
                            onChange={(e) => {
                              setDropShipStreet1(e.target.value);
                              if (e.target.value) setDropShipStreet1Error(false);
                            }}
                            onBlur={() => setDropShipStreet1Error(!dropShipStreet1)}
                            className={`w-full ${dropShipStreet1Error ? 'p-invalid' : ''}`}
                          />
                          {dropShipStreet1Error && (
                            <small className="p-error">{t('StreetAddress1IsRequired')}</small>
                          )}
                        </div>

                        <div className="col-12 lg:col-6 field mb-3">
                          <label htmlFor="dropShipCity" className="text-900 font-medium mb-2 block">
                            {t('City')} *
                          </label>
                          <InputText
                            id="dropShipCity"
                            maxLength={30}
                            value={dropShipCity}
                            onChange={(e) => {
                              setDropShipCity(e.target.value);
                              if (e.target.value) setDropShipCityError(false);
                            }}
                            onBlur={() => setDropShipCityError(!dropShipCity)}
                            className={`w-full ${dropShipCityError ? 'p-invalid' : ''}`}
                          />
                          {dropShipCityError && (
                            <small className="p-error">{t('CityIsRequired')}</small>
                          )}
                        </div>
                        <div className="col-12 lg:col-6 field mb-3">
                          <label htmlFor="dropShipZip" className="text-900 font-medium mb-2 block">
                            {t('ZipOrPostalCode')} *
                          </label>
                          <InputText
                            id="dropShipZip"
                            maxLength={10}
                            value={dropShipZip}
                            onChange={(e) => {
                              setDropShipZip(e.target.value);
                              if (e.target.value) setDropShipZipError(false);
                            }}
                            onBlur={() => setDropShipZipError(!dropShipZip)}
                            className={`w-full ${dropShipZipError ? 'p-invalid' : ''}`}
                          />
                          {dropShipZipError && (
                            <small className="p-error">{t('ZipOrPostalCodeIsRequired')}</small>
                          )}
                        </div>

                        <div className="col-12 lg:col-6 field mb-3">
                          <label
                            htmlFor="dropShipCountry"
                            className="text-900 font-medium mb-2 block"
                          >
                            {t('Country')} *
                          </label>
                          <Dropdown
                            id="dropShipCountry"
                            value={dropShipCountry}
                            onChange={(e) => {
                              setDropShipCountry(e.value);
                              setDropShipCountryError(false);
                            }}
                            onHide={() => setDropShipCountryError(!dropShipCountry)}
                            options={countries}
                            optionValue="iso2"
                            placeholder={t('SelectACountry')}
                            className={`w-full ${dropShipCountryError ? 'p-invalid' : ''}`}
                          />
                          {dropShipCountryError && (
                            <small className="p-error">{t('CountryIsRequired')}</small>
                          )}
                        </div>

                        <div className="col-12 lg:col-6 field mb-3">
                          <label
                            htmlFor="dropShipState"
                            className="text-900 font-medium mb-2 block"
                          >
                            {t('StateOrProvince')} *
                          </label>
                          <Dropdown
                            id="dropShipState"
                            value={dropShipState}
                            onChange={(e) => {
                              setDropShipState(e.value);
                              setDropShipStateError(false);
                            }}
                            onHide={() => setDropShipStateError(!dropShipState)}
                            options={states}
                            placeholder={t('SelectAStateProvince')}
                            className={`w-full ${dropShipStateError ? 'p-invalid' : ''}`}
                          />
                          {dropShipStateError && (
                            <small className="p-error">{t('StateOrProvinceIsRequired')}</small>
                          )}
                        </div>
                      </div>
                    </TabPanel>
                  </TabView>
                ) : (
                  <div className="grid">
                    <div className="col-12 field mb-0">
                      <label htmlFor="addresses" className="text-900 font-medium mb-3"></label>
                      <Dropdown
                        value={commonCtx.selectedShippingAddress?.ID}
                        onChange={(e) => {
                          commonCtx.selectShippingAddress(e.value);
                        }}
                        disabled={commonCtx.loadingCart}
                        className="p-inputtext w-full mb-3"
                        options={commonCtx.addresses.filter((address) => address.Billing === false)}
                        optionLabel="label"
                        placeholder={t('ChooseAddress')}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="hidden">
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="street1" className="text-900 font-medium mb-3">
                    {t('Street1')}
                  </label>
                  <input
                    id="name2"
                    readOnly
                    type="text"
                    value={
                      commonCtx.selectedShippingAddress?.Street1
                        ? commonCtx.selectedShippingAddress?.Street1
                        : ''
                    }
                    className="p-inputtext w-full mb-3"
                  />
                </div>
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="street2" className="text-900 font-medium mb-3">
                    {t('Street2')}
                  </label>
                  <input
                    id="surname2"
                    readOnly
                    value={
                      commonCtx.selectedShippingAddress?.Street2
                        ? commonCtx.selectedShippingAddress?.Street2
                        : ''
                    }
                    type="text"
                    className="p-inputtext w-full mb-3"
                  />
                </div>
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="pc2" className="text-900 font-medium mb-3">
                    {t('PostalCode')}
                  </label>
                  <input
                    id="zip"
                    type="text"
                    readOnly
                    value={
                      commonCtx.selectedShippingAddress?.Zip
                        ? commonCtx.selectedShippingAddress?.Zip
                        : ''
                    }
                    className="p-inputtext w-full mb-3"
                  />
                </div>
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="city2" className="text-900 font-medium mb-3">
                    {t('City')}
                  </label>
                  <input
                    id="city2"
                    value={
                      commonCtx.selectedShippingAddress?.City
                        ? commonCtx.selectedShippingAddress?.City
                        : ''
                    }
                    readOnly
                    type="text"
                    className="p-inputtext w-full mb-3"
                  />
                </div>
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="pc2" className="text-900 font-medium mb-3">
                    {t('Phone')}
                  </label>
                  <input
                    id="phone"
                    type="text"
                    readOnly
                    value={
                      commonCtx.selectedShippingAddress?.Phone
                        ? commonCtx.selectedShippingAddress?.Phone
                        : ''
                    }
                    className="p-inputtext w-full mb-3"
                  />
                </div>
                <div className="col-12 lg:col-6 field mb-0">
                  <label htmlFor="city2" className="text-900 font-medium mb-3">
                    {t('Country')}
                  </label>
                  <input
                    id="country2"
                    readOnly
                    type="text"
                    value={
                      commonCtx.selectedShippingAddress?.Country
                        ? commonCtx.selectedShippingAddress?.Country
                        : ''
                    }
                    className="p-inputtext w-full mb-3"
                  />
                </div>
              </div>
              <Divider className="hidden w-full px-2 mb-3" />

              <div className="col-12 text-900 font-medium text-xl mt-4 lg:mt-0 mb-4 px-2">
                {t('AddtionalInfo')}
              </div>
              <div className="col-12 lg:col-3 field mb-0">
                <label htmlFor="pc2" className="text-900 font-medium mb-3">
                  {t('PreferredDeliveryDate')} *
                </label>
                <Calendar
                  className=" w-full mb-3"
                  value={RequestedDeliveryDate}
                  showIcon
                  minDate={new Date()}
                  dateFormat={commonCtx.dateFormat}
                  onChange={(e) => {
                    commonCtx.calculatedOrder = null;
                    setRequestedDeliveryDate(e.value);
                  }}
                ></Calendar>
              </div>
              <div className="col-12 lg:col-3 field mb-0">
                <label htmlFor="city2" className="text-900 font-medium mb-3">
                  {t('CustomerPO')} *
                </label>
                <input
                  id="customerpo"
                  required
                  type="text"
                  maxLength="15"
                  className="p-inputtext w-full mb-3"
                  value={CustomerPO}
                  onChange={(e) => {
                    commonCtx.calculatedOrder = null;
                    setCustomerPO(e.target.value);
                  }}
                />
              </div>
              <div className="col-12 lg:col-6 field mb-0">
                <label htmlFor="city2" className="text-900 font-medium mb-3">
                  {t('Notes')}
                </label>
                <InputTextarea
                  value={Notes}
                  maxLength="256"
                  onChange={(e) => {
                    commonCtx.calculatedOrder = null;
                    setNotes(e.target.value);
                  }}
                  className="p-inputtext w-full mb-3"
                  rows={1}
                  cols={30}
                  autoResize
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 md:col-2">
          <div className="card card-w-title global-sales ui-fluid">
            <div className="text-900 font-medium text-xl mb-2">{t('Checkout Summary')}</div>
            <div className="text-800 font-medium">
              <b>{commonCtx.lineItems?.length}</b>
              {' ' + t('ProductsInCart')}{' '}
            </div>
            <div className="text-500 font-medium mt-2">
              (<b>{commonCtx?.totalProductsInCart}</b>
              {' ' + t('ItemsInTotal')})
            </div>
            <hr className="  my-3 mx-0 border-top-1 border-none surface-border" />

            <div className="flex align-items-center mb-4">
              {t('Subtotal')}
              <span className="font-bold text-2xl text-900 ml-2">
                {commonCtx?.activeCart?.Subtotal
                  ? commonCtx.showPrice
                    ? formatPrice(commonCtx?.activeCart?.Subtotal, commonCtx.locale)
                    : '-'
                  : '-'}
              </span>
            </div>
            {commonCtx?.calculatedOrder?.TaxCost > 0 && false && (
              <div className="flex align-items-center mb-4">
                {t('Taxes')}
                <span className="font-bold text-2xl text-900 ml-2">
                  {formatPrice(commonCtx?.calculatedOrder?.TaxCost, commonCtx.locale)}
                </span>
              </div>
            )}
            <div className="flex align-items-center mb-4">
              {t('Total')}
              <span
                className={
                  commonCtx?.calculatedOrder
                    ? 'font-bold text-2xl text-900 ml-2'
                    : 'font-bold text-l text-900 ml-2'
                }
              >
                {commonCtx?.calculatedOrder
                  ? formatPrice(commonCtx?.calculatedOrder?.Total, commonCtx.locale)
                  : t('PleasePressCalculate')}
              </span>
            </div>
            <hr className="hidden my-3 mx-0 border-top-1 border-none surface-border" />
            <ul className="hidden list-none p-0 m-0 flex-grow-1">
              <li className="flex align-items-center mb-3">
                <i className="pi pi-check-circle text-green-500 mr-2"></i>
                <span>
                  {t('ShippingCost')}: &nbsp;
                  <b>
                    {commonCtx.currencySymbol}{' '}
                    {commonCtx?.calculatedOrder ? commonCtx?.calculatedOrder?.ShippingCost : 0}
                  </b>
                </span>
              </li>
              <li className="flex align-items-center mb-3">
                <i className="pi pi-check-circle text-green-500 mr-2"></i>
                <span>
                  {t('AllItemAre')}

                  <b>{t('InStock')}</b>
                </span>
              </li>
            </ul>
            <hr className="hidden mb-3 mx-0 border-top-1 border-none surface-border mt-auto" />

            <div className="grid">
              <div className="col-2">
                <Button
                  type="button"
                  onClick={() => goToCart()}
                  icon="pi pi-angle-left"
                  className="p-button-outlined  p-3  w-full  "
                />
              </div>
              <div className="col-10">
                <button
                  disabled={
                    !commonCtx.lineItems?.length ||
                    (activeTab === 0 &&
                      (!commonCtx.selectedShippingAddress ||
                        commonCtx.selectedShippingAddress?.ID === null)) ||
                    (activeTab === 1 && !isDropShipFormValid()) ||
                    !CustomerPO ||
                    CustomerPO === null ||
                    CustomerPO === '' ||
                    commonCtx.prevalidationStatus === 'pending'
                  }
                  aria-label="Pre-validate Taxes"
                  onClick={() => {
                    const selectedCountry = countries.find((c) => c.iso2 === dropShipCountry);
                    commonCtx.initPrevalidation(
                      CustomerPO,
                      Notes,
                      RequestedDeliveryDate,
                      activeTab,
                      {
                        name: dropShipName,
                        street1: dropShipStreet1,
                        street2: dropShipStreet2,
                        city: dropShipCity,
                        state: dropShipState,
                        zip: dropShipZip,
                        country: selectedCountry?.value || '',
                        countryISO2: dropShipCountry,
                      }
                    );
                  }}
                  className="p-button p-component p-3 w-full"
                >
                  {commonCtx.prevalidationStatus === 'pending' ? (
                    <>
                      <i className="pi pi-spin pi-spinner mr-2" />
                      {t('PreValidationPending')}
                    </>
                  ) : (
                    <span className="p-button-label p-c">{t('Calculate')}</span>
                  )}
                </button>
              </div>
            </div>
            <Button
              label={t('BuyNow')}
              disabled={commonCtx.calculatedOrder === null || CustomerPO === ''}
              aria-label="Buy Now"
              className="p-button-success p-3 mt-4 w-12  "
              onClick={() => commonCtx.sendOrder()}
            />
          </div>
        </div>
      </div>
      <Cart fromCheckout="true"></Cart>
    </>
  );
};

export default Checkout;
